import * as React from "react"
import { Check, Copy, Heart } from "lucide-react"

import { AlbumArt } from "@/components/album-art"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { buildRequestCommand } from "@/config"
import { useCopyCommand } from "@/hooks/use-copy-command"
import { cn } from "@/lib/utils"
import type { Song } from "@/types/song"

interface SongCardProps {
  song: Song
  isFavorite: boolean
  onToggleFavorite: (id: number) => void
}

export function SongCard({
  song,
  isFavorite,
  onToggleFavorite,
}: SongCardProps) {
  const { copiedId, copy } = useCopyCommand()
  const command = buildRequestCommand(song.requestCode)
  const isCopied = copiedId === song.id
  const isNew = (song.genre ?? "").includes("NEW(신곡)")

  const handleCopy = React.useCallback(
    (event?: React.SyntheticEvent) => {
      event?.stopPropagation()
      void copy(song.id, command)
    },
    [copy, song.id, command]
  )

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      handleCopy()
    }
  }

  return (
    <Card
      className="group/song-card pt-0 pb-4 transition-shadow hover:shadow-lg"
      size="sm"
      role="button"
      tabIndex={0}
      onClick={handleCopy}
      onKeyDown={handleKeyDown}
      aria-label={`${song.title} - ${song.artist}, 신청 명령어 복사`}
    >
      <div className="relative">
        <AlbumArt song={song} className="rounded-t-4xl" />

        {isNew && (
          <Badge className="absolute top-2 left-2 bg-primary text-primary-foreground shadow">
            NEW
          </Badge>
        )}

        <button
          type="button"
          aria-label={isFavorite ? "즐겨찾기 해제" : "즐겨찾기 추가"}
          aria-pressed={isFavorite}
          onClick={(event) => {
            event.stopPropagation()
            onToggleFavorite(song.id)
          }}
          className="absolute top-2 right-2 flex size-8 items-center justify-center rounded-full bg-background/80 shadow backdrop-blur transition-colors hover:bg-background"
        >
          <Heart
            className={cn(
              "size-4",
              isFavorite
                ? "fill-destructive text-destructive"
                : "text-foreground/70"
            )}
          />
        </button>

        <Badge
          variant="secondary"
          className="absolute right-2 bottom-2 bg-background/90 shadow"
        >
          🧀 치즈 {song.price ? `${song.price}개` : "무료"}
        </Badge>
      </div>

      <CardContent className="flex flex-col gap-0.5 pt-3">
        <p className="line-clamp-2 leading-snug font-medium wrap-break-word">
          {song.title}
        </p>
        <p className="truncate text-sm text-muted-foreground">{song.artist}</p>
      </CardContent>

      <CardFooter className="mt-1">
        <button
          type="button"
          onClick={handleCopy}
          aria-label="신청 명령어 복사"
          className="flex w-full items-center justify-between gap-2 rounded-2xl bg-muted px-3 py-2 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
        >
          <span className="truncate font-mono">{command}</span>
          {isCopied ? (
            <Check className="size-4 shrink-0 text-primary" />
          ) : (
            <Copy className="size-4 shrink-0" />
          )}
        </button>
      </CardFooter>
    </Card>
  )
}
